import useSWR from 'swr';
import api from '../services/api';

const SWR_OPTIONS = {
  errorRetryCount: 3,
  revalidateOnFocus: false,
  shouldRetryOnError: false,
};

// Voice-survey call log, paginated and optionally filtered by status or account.
// Pass skip to defer the request (e.g. until the account id is known).
export const useSurveySessions = ({ page = '1', status = '', accountId = '', limit = '', skip = false }) => {
  const params = new URLSearchParams({ page });
  if (status) {
    params.set('status', status);
  }
  if (accountId) {
    params.set('account_id', accountId);
  }
  if (limit) {
    params.set('limit', limit);
  }
  const { data: response, error } = useSWR(
    skip ? null : `/v1/admin/survey_sessions?${params.toString()}`,
    api,
    SWR_OPTIONS
  );

  return {
    loading: !skip && !response && !error,
    data: response ? response.data.results : null,
    meta: response ? response.data.meta : null,
  };
};

// Aggregate call metrics for the last 30 days.
export const useSurveySessionStats = () => {
  const { data: response } = useSWR('/v1/admin/survey_sessions/stats', api, SWR_OPTIONS);
  return { stats: response ? response.data.results : null };
};

// One call with its answers, transcript and webhook events.
export const useSurveySession = (id) => {
  const { data: response, error } = useSWR(
    id ? `/v1/admin/survey_sessions/${id}` : null,
    api,
    SWR_OPTIONS
  );

  return {
    loading: !response && !error,
    session: response ? response.data.results : null,
  };
};

// Presigned URL for one call's recording. Nothing is requested until enabled
// (the admin pressed play); the URL expires after 15 minutes, so callers use
// refresh() to get a fresh one.
export const useSurveySessionRecording = (id, { enabled }) => {
  const { data: response, error, mutate } = useSWR(
    enabled && id ? `/v1/admin/survey_sessions/${id}/recording` : null,
    api,
    SWR_OPTIONS
  );

  return {
    loading: enabled && !response && !error,
    recording: response ? response.data.results : null,
    error,
    refresh: mutate,
  };
};

// Polled while the call is live, so the list follows the caller's answers.
const LIVE_REFRESH_MS = 15000;

const postRecommendations = (url, body) => api.post(url, body);

// What we would recommend to the account now, with the given call's facts
// overlaid (the API also picks up a call still in progress by itself).
export const useAccountRecommendations = (accountId, { surveySessionId, sort, live }) => {
  const { data: response, error } = useSWR(
    accountId
      ? [`/v1/admin/accounts/${accountId}/recommendations`, { surveySessionId, sort }]
      : null,
    postRecommendations,
    { ...SWR_OPTIONS, refreshInterval: live ? LIVE_REFRESH_MS : 0 }
  );

  return {
    loading: !response && !error,
    recommendations: response ? response.data.results : null,
    error,
  };
};
